import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import ButtonWithChangeState from '../../Components/common/ButtonWithChangeState/ButtonWithChangeState';
import { cvUpdate, restoreAfterRefresh } from '../../actions/uploadData';
import contractor from '../../Assets/images/image 27.png';
import permanent from '../../Assets/images/image 28.png';
import relocation from '../../Assets/images/image 29.png';
import remote from '../../Assets/images/image 31.png';
import ticked from '../../Assets/images/Group 62.png';
import { ContractForm, PermanentForm } from '../../Components/pages/profileBuilder.Six';
import ValidationErrorLabel from '../../Components/common/ValidationErrorLabel/ValidationErrorLabel';
import { cvParser } from '../../utils/dataParsers';
import api from '../../api/apiSettings';
import ProfileBuilderWrapper from '../../Components/pages/profileBuilder.Wrapper/ProfileBuilderWrapper';
import { parseAPIErrors } from '../../utils/helpers';
import BaseModal from '../../Components/common/ModalWindows/BaseModal';
import { maxMoneyValue, MOBILE_BREAKPOINT, validationMessages } from '../../utils/constants';
import { useWindowSize } from '../../utils/hooks';

function ProfileBuilderSix() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const size = useWindowSize();
  const [t] = useTranslation();
  const validationMessage = useMemo(() => validationMessages(t), [t]);

  const sourceCV = useSelector((state) => state.uploadData.parsedCV);
  const relocationState = useSelector((state) => state.uploadData.parsedCV.JobsFilters.relocationWork);
  const remoteState = useSelector((state) => state.uploadData.parsedCV.JobsFilters.remoteWork);
  const contractValues = useSelector((state) => state.uploadData.parsedCV.JobsFilters.contract);
  const permanentValues = useSelector((state) => state.uploadData.parsedCV.JobsFilters.permanent);
  const [errorsFromApi, setErrorsFromApi] = useState(null);

  const [pending, setPending] = useState(false);

  const validationSchema = yup.object({
    hourly: yup
      .string()
      .test('more-than-zero', validationMessage.HOURLY_OR_DAILY, (value, ctx) =>
        contractValues.contrActive ? !!ctx.parent.daily || !!value : true
      )
      .test('only-numbers', validationMessage.ONLY_NUMBERS, (value) => value === undefined || value?.match(/^\d+$/))
      .test('max-value', validationMessage.MAX_MONEY_VALUE, (value) => (value ? Number(value) <= maxMoneyValue : true)),
    daily: yup
      .string()
      .test('more-than-zero', validationMessage.HOURLY_OR_DAILY, (value, ctx) =>
        contractValues.contrActive ? !!ctx.parent.hourly || !!value : true
      )
      .test('only-numbers', validationMessage.ONLY_NUMBERS, (value) => value === undefined || value?.match(/^\d+$/))
      .test('max-value', validationMessage.MAX_MONEY_VALUE, (value) => (value ? Number(value) <= maxMoneyValue : true)),
    salary: yup
      .string()
      .test('more-than-zero', validationMessage.SALARY_REQ, (value) => (permanentValues.permActive ? !!value : true))
      .test('only-numbers', validationMessage.ONLY_NUMBERS, (value) => value === undefined || value?.match(/^\d+$/))
      .test('max-value', validationMessage.MAX_MONEY_VALUE, (value) => (value ? Number(value) <= maxMoneyValue : true)),
    currency: yup
      .string()
      .test('more-than-zero', validationMessage.CURRENCY_REQ, (value) =>
        contractValues.contrActive || permanentValues.permActive ? !!value : true
      ),
    contrActive: yup
      .boolean()
      .test('more-than-zero', `Required`, () => contractValues.contrActive || permanentValues.permActive)
  });

  const onSubmit = async (values) => {
    setPending(true);
    const parsedCV = cvParser({
      ...sourceCV,
      JobsFilters: {
        ...sourceCV.JobsFilters,
        contract: {
          contrActive: sourceCV.JobsFilters.contract.contrActive,
          hourly: values.hourly,
          daily: values.daily,
          currency: values.currency
        },
        permanent: {
          permActive: sourceCV.JobsFilters.permanent.permActive,
          salary: values.salary
        }
      }
    });

    const userId = localStorage.getItem('UserId');

    try {
      setErrorsFromApi(null);
      await api.put(`v1/user/${userId}`, parsedCV);

      const jobForSaving = localStorage.getItem('saveJobAfterCompleteProfile');
      if (jobForSaving) {
        await api.post(`v1/job/save-job`, { JobId: JSON.parse(jobForSaving), UserId: userId });
        localStorage.removeItem('saveJobAfterCompleteProfile');
      }

      window.location.href = '/dashboard/profile-builder-seven';
      localStorage.setItem('userProfileCompleted', 'true');
    } catch (e) {
      setPending(false);
      setErrorsFromApi(typeof e.response.data === 'string' ? e.response.data : parseAPIErrors(e.response.data.errors));
    }
  };

  const preferencesForm = useFormik({
    initialValues: { ...contractValues, ...permanentValues },
    validationSchema,
    onSubmit: (values) => onSubmit(values)
  });

  const setFormToState = () => {
    dispatch(cvUpdate({ flag: 'Preferences', value: preferencesForm.values }));
  };

  const backHandler = () => {
    setFormToState();
    navigate('/dashboard/profile-builder-five');
  };

  const handleRefresh = () => {
    localStorage.setItem(
      'forRestoreStore',
      JSON.stringify({
        ...sourceCV,
        JobsFilters: {
          ...sourceCV.JobsFilters,
          contract: {
            contrActive: sourceCV.JobsFilters.contract.contrActive,
            hourly: preferencesForm.values.hourly,
            daily: preferencesForm.values.daily,
            currency: preferencesForm.values.currency
          },
          permanent: {
            permActive: sourceCV.JobsFilters.permanent.permActive,
            salary: preferencesForm.values.salary
          }
        }
      })
    );
  };

  const restoreData = () => {
    const data = localStorage.getItem('forRestoreStore');

    if (data) {
      dispatch(restoreAfterRefresh({ ...JSON.parse(data) }));
      localStorage.setItem('forRestoreStore', '');
    }
  };

  useEffect(() => {
    window.addEventListener('beforeunload', handleRefresh);
    restoreData();
    return () => {
      window.removeEventListener('beforeunload', handleRefresh);
    };
  }, [sourceCV.JobsFilters]);

  return (
    <ProfileBuilderWrapper
      titleMain={t('profile.builder.start.complete')}
      titleSecondary={t('profile.builder.jobpreferences')}
      text={t('profile.builder.chargerate')}
      progress={100}
      loading={pending}
    >
      <div className="flex w-full py-4">
        <div className="flex flex-col w-full text-start">
          <div className="d-block p-0 p-sm-3 px-0 px-sm-3">
            <h6>{t('profile.builder.interestedin')}</h6>
            <hr />
            {preferencesForm.touched.contrActive && preferencesForm.errors.contrActive && (
              <div className="d-flex justify-content-center mb-3">
                <ValidationErrorLabel message={validationMessage.TYPE_WORK_CHOOSE_ONE} />
              </div>
            )}
            <div className="flex justify-evenly py-4">
              <div className="flex flex-col align-items-center">
                <ButtonWithChangeState
                  isOpen={contractValues.contrActive}
                  openImg={ticked}
                  closeImg={contractor}
                  changeStateHandler={() => {
                    dispatch(cvUpdate({ flag: 'Contract' }));
                    preferencesForm.setFieldValue('hourly', '');
                    preferencesForm.setFieldValue('daily', '');
                  }}
                  label={t('profile.builder.contractwork')}
                >
                  <div className="mb-3">
                    <ContractForm
                      values={preferencesForm.values}
                      changeHandler={preferencesForm.setFieldValue}
                      errors={preferencesForm.errors}
                      touched={preferencesForm.touched}
                    />
                  </div>
                </ButtonWithChangeState>
              </div>
              <div className="col-md-6 d-flex flex-column align-items-center">
                <ButtonWithChangeState
                  isOpen={permanentValues.permActive}
                  openImg={ticked}
                  closeImg={permanent}
                  changeStateHandler={() => {
                    dispatch(cvUpdate({ flag: 'Permanent' }));
                    preferencesForm.setFieldValue('salary', '');
                  }}
                  label={t('profile.builder.permenantwork')}
                >
                  <div className="mb-3 w-75 w-sm-auto">
                    <PermanentForm
                      values={preferencesForm.values}
                      changeHandler={preferencesForm.setFieldValue}
                      errors={preferencesForm.errors}
                      touched={preferencesForm.touched}
                    />
                  </div>
                </ButtonWithChangeState>
              </div>
            </div>
            <h6 className="pt-5">{t('profile.builder.opento')}</h6>
            <hr />
            <div className="flex justify-evenly py-4">
              <div className="flex flex-col align-items-center">
                <ButtonWithChangeState
                  isOpen={relocationState}
                  openImg={ticked}
                  closeImg={relocation}
                  changeStateHandler={() => dispatch(cvUpdate({ flag: 'Relocation' }))}
                  label={t('profile.builder.relocation')}
                />
              </div>
              <div className="flex flex-col align-items-center">
                <ButtonWithChangeState
                  isOpen={remoteState}
                  openImg={ticked}
                  closeImg={remote}
                  changeStateHandler={() => dispatch(cvUpdate({ flag: 'Remote' }))}
                  label={t('profile.builder.remotework')}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={size.width > MOBILE_BREAKPOINT ? 'flex flex-row pt-5 justify-between' : 'flex flex-col-reverse justify-between'}>
        <div className={size.width > MOBILE_BREAKPOINT ? 'flex' : 'flex justify-center'}>
          <button onClick={backHandler} className="outline-button text-decoration-none p-3 px-5 mt-4">
            <span className="d-inline text-capitalize">{t('common.general.back')}</span>
          </button>
        </div>
        <div
          className={
            size.width > MOBILE_BREAKPOINT
              ? 'text-start flex justify-end'
              : 'flex justify-center'
          }
        >
          <button onClick={preferencesForm.handleSubmit} className="button p-3 px-5 mt-4">
            <span className="d-inline text-capitalize">{t('common.general.submit')}</span>
          </button>
        </div>
      </div>
      <BaseModal
        open={errorsFromApi}
        title={t('profile.builder.errors.clarifydata')}
        cancelBtnTxt="OK"
        onlyReject
        closeHandler={() => setErrorsFromApi(null)}
      >
        <ul>
          {typeof errorsFromApi !== 'string' ? (
            errorsFromApi?.map((error) => (
              <li className="mb-2" key={error}>
                {error}
              </li>
            ))
          ) : (
            <li className="mb-2">{errorsFromApi}</li>
          )}
        </ul>
      </BaseModal>
    </ProfileBuilderWrapper>
  );
}

export default ProfileBuilderSix;
